/**
 * What "Reset to defaults" restores in the Customise drawer.
 *
 * Deliberately NOT the FALLBACK_* constants. Those are the safety net for an
 * unreadable stored value — they hold today's shell so a failed read never
 * re-arranges a studio. This is the opposite question: what an owner is given
 * back when they ask for the defaults.
 *
 * `layout` is "rail" — Standard. Reset is a way out of an arrangement someone
 * does not want, so it has to land on the shell the product treats as its
 * baseline, not on another of the three new ones. The column default on
 * StudioPreference is "rail" too, so a studio created today opens on Standard
 * and reset returns an owner to what they started with.
 *
 * Only the StudioPreference-backed axes are here. The navigation areas in the
 * drawer live in StudioConfiguration and save immediately, so resetting them
 * from this control would silently rewrite a second store — see
 * components/customise/customise-drawer.tsx.
 */
import { LayoutId } from "@/lib/layouts";
import {
  Density,
  FALLBACK_DENSITY,
  FALLBACK_NAV_MODE,
  FALLBACK_POST_STYLE,
  NavMode,
  PostStyle,
} from "@/lib/layout-axes";
import { DEFAULT_HOME_MODULES, serializeHomeModules } from "@/lib/home-modules";
import { DEFAULT_THEME_KEY } from "@/lib/themes/presets";

export interface CustomiseDefaults {
  layout: LayoutId;
  /** The studio's own type, not a member's choice — "default" is the app font. */
  themeFont: string;
  colorMode: "light" | "dark" | "system";
  nav: NavMode;
  postStyle: PostStyle;
  density: Density;
  /** Serialized the same way the provider stores it, so a reset is comparable. */
  homeModules: string;
  themePreset: string;
}

export const CUSTOMISE_DEFAULTS: CustomiseDefaults = {
  layout: "rail",
  themeFont: "default",
  colorMode: "system",
  nav: FALLBACK_NAV_MODE,
  postStyle: FALLBACK_POST_STYLE,
  density: FALLBACK_DENSITY,
  homeModules: serializeHomeModules(DEFAULT_HOME_MODULES),
  themePreset: DEFAULT_THEME_KEY,
};
